import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { formatMoney, NON_ACCOUNT_METHODS, PAYMENT_METHODS, type CurrencyCode, type PaymentMethod } from '@tengesa/shared';
import { api } from '../../lib/api';
import { todayLocal } from '../../lib/dates';
import { useSession } from '../../app/session';
import { useCurrentBranch } from '../../app/Shell';
import { Badge, Button, Checkbox, Dialog, EmptyState, ErrorNotice, Field, FormError, PageHeader, Panel, Select, Spinner, TextInput, fieldErrors, useToast } from '../../ui';
import { MoneyInput } from '../../ui/inputs';

interface Expense {
  id: string;
  number: string;
  expenseDate: string;
  description: string;
  amountCents: number;
  currency: string;
  method: string;
  reference: string | null;
  status: 'pending' | 'approved' | 'rejected';
  shiftId: string | null;
  categoryName: string;
  branchName: string;
  recordedBy: string;
  recordedByName: string;
  decidedByName: string | null;
  decisionReason: string | null;
  supplierName: string | null;
}

const STATUS = { pending: { label: 'Waiting for approval', tone: 'gold' as const }, approved: { label: 'Approved', tone: 'ok' as const }, rejected: { label: 'Rejected', tone: 'danger' as const } };

export function ExpensesPage() {
  const { me, can } = useSession();
  const cur = me.business.baseCurrency as CurrencyCode;
  const [status, setStatus] = useState('');
  const [adding, setAdding] = useState(false);
  const [deciding, setDeciding] = useState<Expense | null>(null);
  const q = useQuery({ queryKey: ['expenses', status], queryFn: () => api.get<Expense[]>(`/api/expenses${status ? `?status=${status}` : ''}`) });
  const pending = (q.data ?? []).filter((e) => e.status === 'pending');
  return (
    <>
      <PageHeader
        title="Expenses"
        description="Running costs: rent, ZESA, fuel, wages. Cash paid from a till is taken off that till's expected cash."
        actions={can('expenses.record') && <Button variant="primary" icon={<Plus className="size-4" />} onClick={() => setAdding(true)}>Record expense</Button>}
      />
      {can('expenses.approve') && pending.length > 0 && !status && (
        <p className="mb-4 rounded-[8px] bg-gold-soft px-4 py-3 text-gold-ink">
          {pending.length} expense{pending.length === 1 ? '' : 's'} totalling {formatMoney(pending.reduce((a, e) => a + e.amountCents, 0), cur)} waiting for your approval.
        </p>
      )}
      <Panel>
        <div className="border-b border-line px-5 py-3">
          <Select aria-label="Status" className="max-w-60" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All expenses</option>
            <option value="pending">Waiting for approval</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </Select>
        </div>
        {q.isLoading && <Spinner />}
        {q.error && <div className="p-4"><ErrorNotice error={q.error} /></div>}
        {q.data?.length === 0 && <EmptyState title="No expenses recorded" />}
        {!!q.data?.length && (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-left">
              <thead>
                <tr className="border-b border-line text-sm text-muted">
                  <th className="px-5 py-2.5 font-medium">Date</th>
                  <th className="px-3 py-2.5 font-medium">What for</th>
                  <th className="px-3 py-2.5 font-medium">Paid by</th>
                  <th className="px-3 py-2.5 font-medium">Status</th>
                  <th className="px-5 py-2.5 text-right font-medium">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {q.data.map((e) => (
                  <tr key={e.id}>
                    <td className="px-5 py-3 text-sm num">
                      {e.expenseDate}
                      <span className="block text-xs text-muted">{e.number}</span>
                    </td>
                    <td className="px-3 py-3">
                      {e.description}
                      <span className="block text-xs text-muted">
                        {e.categoryName} — {e.branchName}{e.supplierName ? ` — ${e.supplierName}` : ''} — by {e.recordedByName}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-sm">
                      {PAYMENT_METHODS[e.method as PaymentMethod]?.label.split(' (')[0]}
                      {e.shiftId && <span className="block text-xs text-muted">From the till</span>}
                      {e.reference && <span className="block text-xs text-muted">{e.reference}</span>}
                    </td>
                    <td className="px-3 py-3">
                      <Badge tone={STATUS[e.status].tone}>{STATUS[e.status].label}</Badge>
                      {e.decisionReason && <span className="block text-xs text-muted">{e.decisionReason}</span>}
                      {e.status === 'pending' && can('expenses.approve') && e.recordedBy !== me.user.id && (
                        <button className="mt-1 block text-sm font-medium text-ledger-700 hover:underline" onClick={() => setDeciding(e)}>Review</button>
                      )}
                    </td>
                    <td className="px-5 py-3 text-right num">{formatMoney(e.amountCents, e.currency as CurrencyCode)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
      {adding && <ExpenseDialog onClose={() => setAdding(false)} />}
      {deciding && <DecisionDialog expense={deciding} onClose={() => setDeciding(null)} />}
    </>
  );
}

function ExpenseDialog({ onClose }: { onClose: () => void }) {
  const { me } = useSession();
  const { branch } = useCurrentBranch();
  const cur = me.business.baseCurrency as CurrencyCode;
  const qc = useQueryClient();
  const toast = useToast();
  const cats = useQuery({ queryKey: ['expense-categories'], queryFn: () => api.get<{ id: string; name: string }[]>('/api/expense-categories') });
  const suppliers = useQuery({ queryKey: ['suppliers', false], queryFn: () => api.get<{ id: string; name: string }[]>('/api/suppliers'), retry: false });
  const [v, setV] = useState({
    branchId: branch?.id ?? '',
    categoryId: '',
    expenseDate: todayLocal(),
    description: '',
    amountCents: null as number | null,
    method: 'cash' as PaymentMethod,
    reference: '',
    supplierId: '',
    fromTill: true,
  });
  const set = <K extends keyof typeof v>(k: K, val: (typeof v)[K]) => setV((p) => ({ ...p, [k]: val }));
  const m = useMutation({
    mutationFn: () => api.post<{ number: string; status: string }>('/api/expenses', { ...v, supplierId: v.supplierId || null, reference: v.reference || null, fromTill: v.method === 'cash' && v.fromTill }),
    onSuccess: (e) => {
      toast(`${e.number} recorded${e.status === 'pending' ? ' and sent for approval' : ''}`);
      for (const k of ['expenses', 'current-shift', 'dashboard']) void qc.invalidateQueries({ queryKey: [k] });
      onClose();
    },
  });
  const f = fieldErrors(m.error);
  return (
    <Dialog
      open
      wide
      onClose={onClose}
      title="Record an expense"
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!v.categoryId || !v.amountCents || v.description.trim().length < 3} busy={m.isPending} onClick={() => m.mutate()}>Record expense</Button>
        </>
      }
    >
      <FormError error={m.error} />
      <div className="grid gap-4 sm:grid-cols-3">
        <Field label="Category" htmlFor="e-cat" error={f.categoryId}>
          <Select id="e-cat" value={v.categoryId} onChange={(e) => set('categoryId', e.target.value)}>
            <option value="">Choose…</option>
            {cats.data?.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </Select>
        </Field>
        <Field label="Date" htmlFor="e-date" error={f.expenseDate}>
          <TextInput id="e-date" type="date" value={v.expenseDate} onChange={(e) => set('expenseDate', e.target.value)} />
        </Field>
        <Field label="Branch" htmlFor="e-branch">
          <Select id="e-branch" value={v.branchId} onChange={(e) => set('branchId', e.target.value)}>
            {me.branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </Select>
        </Field>
        <Field label="What was it for?" htmlFor="e-desc" error={f.description} className="sm:col-span-2">
          <TextInput id="e-desc" value={v.description} onChange={(e) => set('description', e.target.value)} placeholder="e.g. 20 litres of diesel for the generator" />
        </Field>
        <Field label="Amount" htmlFor="e-amt" error={f.amountCents}>
          <MoneyInput id="e-amt" currency={cur} value={v.amountCents} onChange={(c) => set('amountCents', c)} />
        </Field>
        <Field label="Paid by" htmlFor="e-method">
          <Select id="e-method" value={v.method} onChange={(e) => set('method', e.target.value as PaymentMethod)}>
            {NON_ACCOUNT_METHODS.map((x) => <option key={x} value={x}>{PAYMENT_METHODS[x].label}</option>)}
          </Select>
        </Field>
        {PAYMENT_METHODS[v.method].needsReference && (
          <Field label="Reference" htmlFor="e-ref" error={f.reference}>
            <TextInput id="e-ref" value={v.reference} onChange={(e) => set('reference', e.target.value)} />
          </Field>
        )}
        {suppliers.data && (
          <Field label="Paid to (optional)" htmlFor="e-sup">
            <Select id="e-sup" value={v.supplierId} onChange={(e) => set('supplierId', e.target.value)}>
              <option value="">—</option>
              {suppliers.data.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </Select>
          </Field>
        )}
        {v.method === 'cash' && (
          <div className="sm:col-span-3">
            <Checkbox label="The cash came out of my open till" description="It is then deducted from what the till should hold at closing." checked={v.fromTill} onChange={(x) => set('fromTill', x)} />
          </div>
        )}
      </div>
    </Dialog>
  );
}

function DecisionDialog({ expense, onClose }: { expense: Expense; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [reason, setReason] = useState('');
  const m = useMutation({
    mutationFn: (approve: boolean) => api.post(`/api/expenses/${expense.id}/decision`, { approve, reason: reason || null }),
    onSuccess: (_d, approve) => {
      toast(`${expense.number} ${approve ? 'approved' : 'rejected'}`);
      for (const k of ['expenses', 'dashboard']) void qc.invalidateQueries({ queryKey: [k] });
      onClose();
    },
  });
  return (
    <Dialog
      open
      onClose={onClose}
      title={`${expense.number}: ${formatMoney(expense.amountCents, expense.currency as CurrencyCode)}`}
      description={`${expense.description} (${expense.categoryName}), recorded by ${expense.recordedByName}.`}
      footer={
        <>
          <Button variant="danger" disabled={reason.trim().length < 3} busy={m.isPending && m.variables === false} onClick={() => m.mutate(false)}>Reject</Button>
          <Button variant="primary" busy={m.isPending && m.variables === true} onClick={() => m.mutate(true)}>Approve</Button>
        </>
      }
    >
      <FormError error={m.error} />
      <Field label="Comment" htmlFor="dec-reason" hint="Required when rejecting.">
        <TextInput id="dec-reason" value={reason} onChange={(e) => setReason(e.target.value)} />
      </Field>
    </Dialog>
  );
}
